import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Search, ExternalLink, Edit, Trash2 } from "lucide-react";
import AdminRoute from "@/components/admin/AdminRoute";

export default function AdminFiles() {
  const files = useQuery((api as any).files.listFiles);
  const updateFileMetadata = useMutation((api as any).files.updateFileMetadata);
  const deleteFile = useMutation((api as any).files.deleteFile);

  const [editingFile, setEditingFile] = useState<any>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const handleEdit = (file: any) => {
    setEditingFile({ ...file });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      await updateFileMetadata({
        id: editingFile._id,
        slug: editingFile.slug,
        alt: editingFile.alt,
        title: editingFile.title,
        description: editingFile.description,
        author: editingFile.author,
        copyright: editingFile.copyright,
        keywords: editingFile.keywords,
      });
      toast.success("File metadata updated");
      setIsDialogOpen(false);
    } catch (error) {
      toast.error("Failed to update metadata");
    }
  };

  const handleDelete = async (id: any) => {
    if (!confirm("Are you sure you want to delete this file?")) return;
    try {
      await deleteFile({ id });
      toast.success("File deleted");
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const filteredFiles = files?.filter((file: any) =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <AdminRoute>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow pt-32 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold">File Management</h1>
              <p className="text-muted-foreground">Manage uploaded files and their SEO metadata</p>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search files..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Preview</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!files ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : filteredFiles?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      No files found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFiles?.map((file: any) => (
                    <TableRow key={file._id}>
                      <TableCell>
                        {file.type.startsWith("image/") ? (
                          <img
                            src={file.url}
                            alt={file.alt || file.name}
                            className="h-12 w-12 object-cover rounded"
                          />
                        ) : (
                          <div className="h-12 w-12 bg-muted rounded flex items-center justify-center text-xs">
                            {file.type.split("/")[1]}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{file.name}</TableCell>
                      <TableCell>{file.type}</TableCell>
                      <TableCell>{file.slug}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => window.open(`/media/${file.slug}`, "_blank")}
                            title="View Public Page"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(file)}
                            title="Edit Metadata"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(file._id)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </main>
        <Footer />

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit File Metadata</DialogTitle>
            </DialogHeader>
            {editingFile && (
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Slug</Label>
                    <Input
                      value={editingFile.slug}
                      onChange={(e) => setEditingFile({ ...editingFile, slug: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Alt Text</Label>
                    <Input
                      value={editingFile.alt || ""}
                      onChange={(e) => setEditingFile({ ...editingFile, alt: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Title (Meta Title)</Label>
                  <Input
                    value={editingFile.title || ""}
                    onChange={(e) => setEditingFile({ ...editingFile, title: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description (Meta Description)</Label>
                  <Textarea
                    value={editingFile.description || ""}
                    onChange={(e) => setEditingFile({ ...editingFile, description: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Author</Label>
                    <Input
                      value={editingFile.author || ""}
                      onChange={(e) => setEditingFile({ ...editingFile, author: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Copyright</Label>
                    <Input
                      value={editingFile.copyright || ""}
                      onChange={(e) => setEditingFile({ ...editingFile, copyright: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Keywords</Label>
                  <Input
                    value={editingFile.keywords || ""}
                    onChange={(e) => setEditingFile({ ...editingFile, keywords: e.target.value })}
                    placeholder="comma, separated, keywords"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminRoute>
  );
}
